import { useId } from 'react'

import { Field } from './Field.tsx'
import { passwordViolations } from './passwordPolicy.ts'
import { PasswordInput } from './PasswordInput.tsx'

/**
 * 비밀번호 입력 — 와이어프레임 `pwField()` 1024행.
 *
 * 🔴 정책 문구를 평소에 쓰지 않는다. **입력값이 규칙을 못 채울 때만** 필드 아래에 나타난다.
 *    eric 이 상시 표시를 거부했다(§1-1 "입력 규칙을 미리 쓰지 않는다").
 *    정책 값과 검사는 `passwordPolicy.ts` 에 있다.
 */
export type PasswordFieldProps = {
  label: string
  value: string
  onChange?: ((value: string) => void) | undefined
  name?: string | undefined
  autoComplete?: string | undefined
  /** 필수 칸 표시(`Field.required`) — 「추가」 창의 규격 */
  required?: boolean | undefined
  /**
   * 오류를 언제 세우나. `'live'`(기본) — 종전대로 치는 동안 정책 위반을 칸 아래에 세운다.
   * `'submit'` — 원자는 스스로 재지 않고 **받은 `error` 만** 세운다(제출 검증 · `useSubmitValidation` · eric 2026-10-27).
   * 그때 빈 칸 · 정책(`passwordViolations`) · 확인 불일치는 화면의 검증기가 잰다.
   */
  validation?: 'live' | 'submit' | undefined
  /** 바깥에서 정한 오류(필수 · 확인 불일치 등). `'live'` 에서도 정책 위반보다 먼저 선다 */
  error?: string | undefined
  /** 입력 id — 제출 검증의 첫 오류 칸 포커스가 이 id 로 찾는다 */
  id?: string | undefined
}

export function PasswordField({
  label,
  value,
  onChange,
  name,
  autoComplete = 'new-password',
  required,
  validation = 'live',
  error: suppliedError,
  id,
}: PasswordFieldProps) {
  const fallbackId = useId()
  const inputId = id ?? fallbackId
  const errorId = `${inputId}-err`
  const violations = validation === 'live' ? passwordViolations(value) : []
  const error =
    suppliedError !== undefined && suppliedError !== ''
      ? suppliedError
      : violations.length > 0
        ? violations.join(' · ')
        : undefined

  return (
    <Field
      // 🔴 입력의 `describedBy` 가 가리키는 id 를 문구에 단다 — 빠지면 문서에 없는 id 를 가리킨다
      errorId={errorId}
      htmlFor={inputId}
      label={label}
      required={required}
      {...(error !== undefined ? { error } : {})}
    >
      {/* 눈 단추(보임/가림)는 `PasswordInput` 이 같이 그린다 — eric 2026-10-02 */}
      <PasswordInput
        autoComplete={autoComplete}
        id={inputId}
        invalid={error !== undefined}
        onChange={onChange ?? (() => undefined)}
        value={value}
        wide
        {...(error !== undefined ? { describedBy: errorId } : {})}
        {...(name !== undefined ? { name } : {})}
      />
    </Field>
  )
}
